import { Drawer, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { GenericAutoComplete } from "../../_components/autocomplete/generic.autocomplete";
import { PrimaryButton } from "../../_components/button/primary.button";
import { DrawerHeader } from "../../_components/drawer/header.drawer";
import { TextValidatorHcs } from "../../_components/textValidator/textValidatorHcs";
import { toastr } from "../../_components/toastr/toastr";
import { getDateString } from "../../_helpers/dateTime.helper";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { userCreditService } from "../../_services/userCredit.service";
import { usersService } from "../../_services/users.service";
import { useDrawerStyles } from "../../_styles/drawer.styles";
import { useNavigate, useParams } from "react-router-dom";
import { GENERAL_STORE } from "../../_stores/general.store";

export const UserCreditManageDrawer = (params: { isOpen: boolean, itemId: number | null, onSave?(): void, onClose?(): void }) => {

    const { userId } = useParams<{ userId: string }>();

    const resetData = () => ({
        id: 0,
        userId: userId ?? "",
        eventData: ""
    });

    const [data, setData] = useState<any>(resetData);

    const navigate = useNavigate();

    useEffect(() => {
        if (params.itemId != null)
            fetchData();
        else
            setData(resetData);
    }, [params.itemId, params.isOpen]);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    function fetchData() {
        if (!params.itemId) return;

        setIsLoading(true)
        return userCreditService
            .get(params.itemId)
            .then(
                (json) => {
                    json.eventDate = getDateString(json.eventDate);
                    setData(json);
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        return userCreditService
            .submit(data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    
                    GENERAL_STORE.refreshUserCredit();

                    if (params.onSave)
                        params.onSave();
                    else
                        navigate("..")
                },
                (e) => toastr.error(e)
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!params.itemId) return;
        setIsLoading(true);
        return userCreditService
            .disable(params.itemId)
            .then(
                (d) => {
                    toastr.success("Disabled successfully");
                    fetchData();

                    GENERAL_STORE.refreshUserCredit();

                    if (params.onSave)
                        params.onSave();

                },
                (e) => {
                    toastr.error(e);
                }
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }))
    };


    return (

        <Drawer
            anchor={"right"}
            variant="persistent"
            open={params.isOpen}
            ModalProps={{
                keepMounted: false
            }}>

            <div
                tabIndex={0}
                role="button">

                <div>

                    <DrawerHeader name={(!!params.itemId) ? "Update User Credit" : "Create User Credit"} onClose={() => { params.onClose ? params.onClose() : navigate("..") }} />

                    <ValidatorForm
                        onSubmit={submit}
                        noValidate>

                        <Grid container spacing={1} sx={useDrawerStyles.container}>

                            <Grid item xs={12}>
                                <GenericAutoComplete
                                    key="userId"
                                    label="User"
                                    hasParent={!!userId}
                                    itemId={data.userId}
                                    parentId={!!userId ? userId : ""}
                                    name={"userId"}
                                    disabled={!!userId}
                                    onItemChange={handleChange}
                                    isRequired={true}
                                    itemsLoader={usersService.dropdown}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    key="eventDate"
                                    name="eventDate"
                                    label="Event Date"
                                    required
                                    fullWidth
                                    type="date"
                                    validators={['required']}
                                    errorMessages={['required field']}
                                    value={data.eventDate}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={1} sx={useDrawerStyles.container}>

                            {(!data.id) &&
                                <Grid item xs={12}>
                                    <PrimaryButton
                                        isSaving={isSaving}
                                        isLoading={isLoading}
                                        type="submit"
                                        variant="save">{"Save"}
                                    </PrimaryButton>
                                </Grid>
                            }

                            {(!!data.id) &&
                                <Grid item xs={12}>
                                    <PrimaryButton
                                        type="button"
                                        variant="danger"
                                        disabled={isLoading}
                                        onClick={disable}
                                        icon="block"
                                    >
                                        Disable
                                    </PrimaryButton>
                                </Grid>
                            }

                        </Grid>

                    </ValidatorForm>
                </div>
            </div>
        </Drawer>
    );
};